import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {BehaviorSubject, debounceTime, distinctUntilChanged, filter, Subject, Subscription} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {Store} from "@ngrx/store";
import {clearFuzzySearch, fuzzySearch} from '../../../state/search/search.actions'
import {
  selectFuzzySearchError,
  selectFuzzySearchLoading,
  selectFuzzySearchResults
} from "../../../state/search/search.selectors";
import {PulseLoadingComponent} from "../../../pages/report/pulse-loading/pulse-loading.component";
import {MatTooltip} from "@angular/material/tooltip";
import {RouterLink} from "@angular/router";
import {tap} from "rxjs/operators";

@Component({
  selector: 'app-search-person-dropdown',
  standalone: true,
  imports: [NgClass, NgForOf, NgIf],
  templateUrl: './search-person-dropdown.component.html',
  styleUrl: './search-person-dropdown.component.scss'
})
export class SearchPersonDropdownComponent implements OnInit, OnDestroy {

  @Input() set query(value: string) {
    // console.log('Input value:', value);
    this.searchSubject.next(value);
  }

  @Input() componentLocation: string = 'reports';

  @Output() resultSelected = new EventEmitter<string>();

  results: any[] = [];
  isLoading = false;
  error: any | null = null;
  message: string | null = null;
  imageErrors: { [key: string]: boolean } = {}; // Track which images failed to load

  // private searchSubject = new Subject<string>();
  private searchSubject = new BehaviorSubject<string>('');

  private subscriptions: Subscription[] = [];

  constructor(private store: Store) {}

  ngOnInit(): void {
    // 1. Observe changes from the input
    const querySubscription = this.searchSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        // tap((q) => console.log('Debounced query:', q))
      )
      .subscribe((query) => this.handleQueryChange(query));
    this.subscriptions.push(querySubscription);

    // 2. Listen for results from the store (no filter so we also get null/empty)
    const resultsSubscription = this.store
      .select(selectFuzzySearchResults)
      .subscribe((searchResults) => {
        // If our current query is < 5 chars, do NOT show any store results
        // (in case some effect or caching tries to repopulate them)
        // You can track the last typed query length in a component property
        if (this.searchSubject.getValue()?.trim().length < 5) {
          this.results = [];
          return;
        }

        if (!searchResults || !searchResults.apollo_result) {
          this.results = [];
        } else {
          this.results = searchResults.apollo_result.people || [];
        }
        this.isLoading = false;
      });

    this.subscriptions.push(resultsSubscription);

    // 3. Listen for loading
    const loadingSubscription = this.store
      .select(selectFuzzySearchLoading)
      .subscribe((loading) => {
        this.isLoading = loading;
      });
    this.subscriptions.push(loadingSubscription);

    // 4. Listen for error
    const errorSubscription = this.store
      .select(selectFuzzySearchError)
      .subscribe((err) => {
        this.error = err;
        if (err) {
          this.isLoading = false;
        }
      });
    this.subscriptions.push(errorSubscription);
  }

  /**
   * Handle logic when the query changes
   */
  private handleQueryChange(query: string): void {
    this.clearResults();

    const length = query.trim().length;
    if (!length) {
      // empty
      this.message = null;
      this.store.dispatch(clearFuzzySearch());
      return;
    }

    if (length < 5) {
      this.message = 'Minimum words to search are 5.';
      this.store.dispatch(clearFuzzySearch());
      return;
    }

    // Otherwise valid
    this.message = null;
    this.isLoading = true;
    this.store.dispatch(fuzzySearch({ query }));
  }

  /**
   * Clear local state only
   */
  clearResults(): void {
    this.results = [];
    this.error = null;
    this.isLoading = false;
    // console.log('Clearing local results.');
  }

  selectResult(result: any): void {
    // Instead of emitting result.name, we emit the linkedin_url
    // (assuming the data for each "result" has a `linkedin_url` field)
    this.resultSelected.emit(result.linkedin_url);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  getPersonAvatar(result: any): string {
    // Check if photo_url exists, is not empty array string, and is not LinkedIn default placeholder
    if (result.photo_url && result.photo_url !== '[]' && !this.isLinkedInDefaultAvatar(result.photo_url)) {
      return result.photo_url;
    }
    // Generate default avatar with person's name
    return this.getDefaultAvatar(result.name);
  }

  private isLinkedInDefaultAvatar(url: string): boolean {
    // Check if the URL is the LinkedIn default placeholder avatar
    return url && url.includes('https://static.licdn.com/aero-v1/sc/h/9c8pery4andzj6ohjkjp54ma2');
  }

  getDefaultAvatar(name: string): string {
    return `https://ui-avatars.com/api/?background=FFA500&color=fff&rounded=true&name=${encodeURIComponent(name)}&format=svg&size=128`;
  }

  /**
   * Handle image loading errors (e.g., expired LinkedIn URLs)
   * @param resultId - Unique identifier for the result (using linkedin_url as ID)
   */
  onImageError(resultId: string): void {
    this.imageErrors[resultId] = true;
  }

  /**
   * Get unique identifier for a result
   * @param result - The search result object
   */
  getResultId(result: any): string {
    return result.linkedin_url || result.name || Math.random().toString();
  }

}
